/**
 * PWA Update Notification Component
 * 
 * Shows a banner when a new app version is available.
 * Allows user to update immediately or dismiss.
 */

import React from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { RefreshCw, X } from 'lucide-react'
import { forceUpdate, getServiceWorkerState } from '@/utils/sw-registration'

interface PWAUpdateBannerProps {
  onDismiss?: () => void
}

export function PWAUpdateBanner({ onDismiss }: PWAUpdateBannerProps) {
  const [isUpdating, setIsUpdating] = React.useState(false)

  const handleUpdate = async () => {
    setIsUpdating(true)
    await forceUpdate()
    // The controllerchange event will trigger a reload
  }

  const handleDismiss = () => {
    onDismiss?.()
  }

  return (
    <Card className="fixed bottom-4 left-4 right-4 z-50 p-4 shadow-lg border-2 border-blue-500 bg-white/95 backdrop-blur-sm md:left-auto md:right-4 md:w-96">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <RefreshCw className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">
            Update Available
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            A new version of the app is ready. Update now for the latest features and fixes.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isUpdating ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Now'
              )}
            </Button>
            <Button
              onClick={handleDismiss}
              variant="outline"
              size="sm"
              disabled={isUpdating}
            >
              Later
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          disabled={isUpdating}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </Card>
  )
}

/**
 * PWA Update Manager Component
 * 
 * Handles update detection and shows notification banner.
 * Use this at the root of your app.
 */
export function PWAUpdateManager() {
  const [showUpdateBanner, setShowUpdateBanner] = React.useState(false)

  React.useEffect(() => {
    // Check if an update is already waiting
    getServiceWorkerState().then((state) => {
      if (state.updateFound) {
        console.log('[PWA] Update detected on mount')
        setShowUpdateBanner(true)
      }
    })

    // Listen for update events from the service worker
    const handleUpdateFound = ((event: CustomEvent) => {
      console.log('[PWA] Update event received:', event.detail)
      setShowUpdateBanner(true)
    }) as EventListener

    window.addEventListener('sw-update-found', handleUpdateFound)

    return () => {
      window.removeEventListener('sw-update-found', handleUpdateFound)
    }
  }, [])

  if (!showUpdateBanner) {
    return null
  }

  return <PWAUpdateBanner onDismiss={() => setShowUpdateBanner(false)} />
}
